import { useCallback } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'

import * as vescajs from '@/apis/vescajs'
import { terminalPortStorageKey, terminalStorageKey } from '@/constants/self-checkin-device-storage'
import { useDeviceSettingForm } from '@/hooks/self-checkin-device-settings/use-device-setting-form'

export type TerminalSettingFormValues = {
  terminalIp: string
  terminalPort: string
}

export const useTerminalSetting = (hotelId: string) => {
  const { t } = useTranslation()
  const { control, isDirty, isLoading, setIsLoading, handleSubmit, reset, cancel } = useDeviceSettingForm(getStoredTerminalSetting)
  const terminalIp = useWatch({ control, name: 'terminalIp' })
  const terminalPort = useWatch({ control, name: 'terminalPort' })

  const connect = useCallback(
    (values: TerminalSettingFormValues, onConnected?: () => void) => {
      const vescaJs = new vescajs.VescaJs({
        hotelId,
        handler: {
          onCancel: () => {
            setIsLoading(false)
            alert(t('Connection has been canceled.'))
          },
          onError: (data?: vescajs.ErrorEvent) => {
            setIsLoading(false)
            if (data) {
              alert(`${t('Could not connect.')}\n[${data.ErrorCode}] ${data.Message}`)
            } else {
              alert(t('Could not connect.'))
            }
          },
          onSuccess: () => {
            setIsLoading(false)
            onConnected?.()
            notification.open({ message: '接続に成功しました' })
          },
        },
        paymentTarget: {},
        config: {
          host: values.terminalIp,
          port: Number(values.terminalPort),
        },
      })

      setIsLoading(true)
      vescaJs.doConnect()
    },
    [hotelId, setIsLoading, t],
  )

  const saveValues = useCallback(
    (values: TerminalSettingFormValues) => {
      if (!values.terminalIp) {
        persistTerminalSetting(values)
        reset(values)
        return
      }

      connect(values, () => {
        persistTerminalSetting(values)
        reset(values)
      })
    },
    [connect, reset],
  )

  // 接続テストは未編集時のみ実行できるため、フォームの値は保存済みの設定と一致する
  const testConnection = useCallback(() => {
    connect({ terminalIp, terminalPort })
  }, [connect, terminalIp, terminalPort])

  return {
    control,
    isDirty,
    isLoading,
    canTestConnection: !!terminalIp,
    testConnection,
    save: handleSubmit(saveValues),
    cancel,
  }
}

const getStoredTerminalSetting = (): TerminalSettingFormValues => ({
  terminalIp: localStorage.getItem(terminalStorageKey) || '',
  terminalPort: localStorage.getItem(terminalPortStorageKey) || '3647',
})

const persistTerminalSetting = (values: TerminalSettingFormValues) => {
  localStorage.setItem(terminalStorageKey, values.terminalIp)
  localStorage.setItem(terminalPortStorageKey, values.terminalPort)
}
